import { merge, uniqBy } from "lodash-es";
import React, { useMemo, useState } from "react";

import {
  IEvent,
  IEventFlightTravelLegParams,
  IEventSuggestedLocation,
  IScheduledFlight,
} from "Bands/types";
import {
  useBandRegionalSettings,
  useIsStartDateBeforeEndDate,
  useOnSubmitTravelLeg,
  useTravelLegLocationOptions,
} from "Bands/utils";
import { FormBuilder } from "shared/components";
import { IAPIError, IDataKeys, IFormButtons } from "shared/types";
import {
  getRegionalSettingsOptions,
  utcStringToDateTimeTimezone,
} from "shared/utils";

import { clearActionMeta, clearDataByKey } from "Bands/state";
import { useDispatch } from "react-redux";
import { space } from "shared/styles/constants";
import { SuggestedLocation } from "..";
import { getFields } from "./formData";

const { defaultTimeZone } = getRegionalSettingsOptions();

interface Props {
  isSubmitting?: boolean;
  error?: string | boolean;
  defaultValues?: IEventFlightTravelLegParams;
  buttons?: IFormButtons;
  apiError?: IAPIError | null;
  handleSubmit: (data: IEventFlightTravelLegParams) => void;
  defaultSuggestedLocations?: IEventSuggestedLocation[];
  suggestedFlights: IScheduledFlight[];
  event: IEvent | null;
}

export const EventFlightTravelLegFormDetails: React.FC<Props> = ({
  buttons,
  handleSubmit,
  error,
  defaultValues: defaultValuesProps,
  defaultSuggestedLocations,
  suggestedFlights,
  event,
  ...rest
}) => {
  const { timeFormat, dateFormat, timeZone } = useBandRegionalSettings();
  const [optionsCopy, setOptionsCopy] = useState<IScheduledFlight[]>();

  const defaultValues = useMemo(
    (): IEventFlightTravelLegParams =>
      merge(
        {},
        {
          departure: {
            date: null,
            timeZone: event?.timeZone || timeZone || defaultTimeZone,
          },
          arrival: {
            date: null,
            timeZone: event?.timeZone || timeZone || defaultTimeZone,
          },
          info: null,
        },
        defaultValuesProps
      ),
    [defaultValuesProps, event?.timeZone, timeZone]
  );

  const startBeforeEndValues = useMemo(
    () => ({
      startDate: defaultValues.departure?.date,
      startTime: defaultValues.departure?.time,
      startTimezone: defaultValues.departure?.timeZone,
      endDate: defaultValues.arrival?.date,
      endTime: defaultValues.arrival?.time,
      endTimezone: defaultValues.arrival?.timeZone,
    }),
    [defaultValues]
  );

  const {
    start,
    end,
    isStartBeforeEnd,
    handleStartDateChange,
    handleEndDateChange,
  } = useIsStartDateBeforeEndDate({
    includeEqual: true,
    defaultValues: startBeforeEndValues,
  });

  const {
    options,
    isFetchingLocations,
    resetLocations,
    setSelectedArrival,
    setSelectedDeparture,
    fetchGoogleLocations,
  } = useTravelLegLocationOptions(defaultSuggestedLocations);

  const { onSubmit } = useOnSubmitTravelLeg(
    handleSubmit,
    start?.time,
    end?.time
  );
  const dispatch = useDispatch();

  /**
   * Format the suggestions from the api to select field values.
   */
  const suggestedLocations = useMemo(() => {
    if (optionsCopy && Boolean(optionsCopy.length)) {
      let suggestedOptions = [
        optionsCopy[0].departureAirport.eventSuggestedLocation,
        optionsCopy[0].arrivalAirport.eventSuggestedLocation,
      ].map((option) => ({
        label: option.location,
        searchableLabel: option.formattedAddress,
        value: option.id,
        labelComponent: (
          <SuggestedLocation {...option} dateFormat={dateFormat} />
        ),
      }));

      return uniqBy([...suggestedOptions, ...options], "label");
    }

    return options;
  }, [optionsCopy, options, dateFormat]);

  const forceUpdateValues = useMemo(() => {
    if (suggestedFlights && suggestedFlights.length > 0) {
      const copySuggestedFlights = suggestedFlights;

      setOptionsCopy(copySuggestedFlights);

      const departure = utcStringToDateTimeTimezone(
        copySuggestedFlights[0].departureTime
      );

      const arrival = utcStringToDateTimeTimezone(
        copySuggestedFlights[0].arrivalTime
      );

      dispatch(clearActionMeta(IDataKeys.scheduledFlight));
      dispatch(clearDataByKey(IDataKeys.scheduledFlight));

      handleStartDateChange({
        timeZone: copySuggestedFlights[0].departureAirport.timeZoneRegionName,
        date: departure.date,
        time: `${departure.time}:00`,
      });

      handleEndDateChange({
        timeZone: copySuggestedFlights[0].arrivalAirport.timeZoneRegionName,
        date: arrival.date,
        time: `${arrival.time}:00`,
      });

      return {
        "departure.location":
          copySuggestedFlights[0].departureAirport.eventSuggestedLocation.id,
        "departure.date": departure.date,
        "departure.time": `${departure.time}:00`,
        "departure.timeZone":
          copySuggestedFlights[0].departureAirport.timeZoneRegionName,
        "arrival.location":
          copySuggestedFlights[0].arrivalAirport.eventSuggestedLocation.id,
        "arrival.date": arrival.date,
        "arrival.time": `${arrival.time}:00`,
        "arrival.timeZone":
          copySuggestedFlights[0].arrivalAirport.timeZoneRegionName,
        airline: copySuggestedFlights[0].carrier.name,
        flightNumber: `${copySuggestedFlights[0].carrier.iata}${copySuggestedFlights[0].flightNumber}`,
      };
    }

    return {};
  }, [dispatch, handleEndDateChange, handleStartDateChange, suggestedFlights]);

  const fields = getFields({
    suggestedLocations: suggestedLocations,
    timeFormat,
    dateFormat,
    isStartBeforeEnd,
    handleStartDateChange,
    handleEndDateChange,
    fetchGoogleLocations,
    isFetchingLocations,
    resetLocations,
    onSelectDeparture: setSelectedDeparture,
    onSelectArrival: setSelectedArrival,
  });

  return (
    <FormBuilder
      fields={fields}
      defaultValues={defaultValues}
      forceUpdateValues={forceUpdateValues}
      onSubmit={onSubmit}
      globalError
      resetForm
      apiErrorMessage={error}
      buttons={buttons}
      style={{ gridTemplateColumns: "repeat(6, 1fr)", marginTop: space.s }}
      {...rest}
    />
  );
};
